import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, ArrowLeft, Trash2, Wallet, TrendingUp, TrendingDown } from "lucide-react";
import { api, money, dateShort } from "../lib/api";

const emptyLay = {
  items: [{ description: "", qty: 1, unit_price: 0 }],
  deposit_amount: 0,
  schedule: "quincenal",
  term_days: 90,
  notes: "",
};

export default function CustomerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [layaways, setLayaways] = useState([]);
  const [creditHistory, setCreditHistory] = useState([]);
  const [open, setOpen] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);
  const [creditForm, setCreditForm] = useState({ amount: "", reason: "", sign: 1 });
  const [form, setForm] = useState(emptyLay);
  const [saving, setSaving] = useState(false);

  const total = form.items.reduce(
    (s, it) => s + (Number(it.qty) || 0) * (Number(it.unit_price) || 0),
    0
  );

  const load = async () => {
    try {
      const [c, ls, ch] = await Promise.all([
        api(`/admin/customers/${id}`),
        api(`/admin/layaways?customer_id=${id}`),
        api(`/admin/customers/${id}/credit-history`),
      ]);
      setCustomer(c);
      setLayaways(ls);
      setCreditHistory(ch);
    } catch (e) {
      toast.error(e.message);
    }
  };

  useEffect(() => {
    load();
     
  }, [id]);

  const updateItem = (i, k, v) => {
    const items = [...form.items];
    items[i] = { ...items[i], [k]: v };
    setForm({ ...form, items });
  };
  const addItem = () =>
    setForm({
      ...form,
      items: [...form.items, { description: "", qty: 1, unit_price: 0 }],
    });
  const removeItem = (i) =>
    setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const submit = async (e) => {
    e.preventDefault();
    if (total <= 0) {
      toast.error("Agrega productos con precio");
      return;
    }
    setSaving(true);
    try {
      const body = {
        customer_id: id,
        items: form.items.map((it) => ({
          ...it,
          qty: Number(it.qty),
          unit_price: Number(it.unit_price),
        })),
        total_amount: Number(total.toFixed(2)),
        deposit_amount: Number(form.deposit_amount || 0),
        schedule: form.schedule,
        term_days: Number(form.term_days),
        notes: form.notes,
      };
      const lay = await api("/admin/layaways", { method: "POST", body });
      toast.success("Apartado creado");
      setForm(emptyLay);
      setOpen(false);
      nav(`/admin/layaways/${lay.id}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("¿Eliminar este cliente? Sus apartados permanecerán.")) return;
    try {
      await api(`/admin/customers/${id}`, { method: "DELETE" });
      toast.success("Cliente eliminado");
      nav("/admin/customers");
    } catch (e) {
      toast.error(e.message);
    }
  };

  const submitCredit = async (e) => {
    e.preventDefault();
    const n = Number(creditForm.amount);
    if (!n || n <= 0) {
      toast.error("Monto inválido");
      return;
    }
    try {
      await api(`/admin/customers/${id}/credit-adjust`, {
        method: "POST",
        body: {
          amount: creditForm.sign === 1 ? n : -n,
          reason: creditForm.reason || (creditForm.sign === 1 ? "Crédito agregado" : "Crédito deducido"),
        },
      });
      toast.success(creditForm.sign === 1 ? `+${money(n)} de crédito` : `${money(n)} deducidos`);
      setCreditForm({ amount: "", reason: "", sign: 1 });
      setCreditOpen(false);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (!customer) return <p className="text-[#8A7D71]">Cargando...</p>;

  return (
    <div data-testid="customer-detail-view" className="space-y-8">
      <Link
        to="/admin/customers"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#8A7D71] hover:text-[#C89B3C] transition-colors"
      >
        <ArrowLeft size={14} /> Clientes
      </Link>

      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.4em] text-[#A66E38] mb-3">
            Cliente
          </p>
          <h1
            data-testid="customer-name"
            className="font-serif text-4xl md:text-5xl font-black text-[#F5F0E6]"
          >
            {customer.name}
          </h1>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setOpen(true)}
            data-testid="new-layaway-btn"
            className="flex items-center gap-2 bg-[#A66E38] hover:bg-[#B88655] text-[#F5F0E6] uppercase tracking-widest text-xs py-3 px-6 transition-colors"
          >
            <Plus size={16} /> Nuevo apartado
          </button>
          <button
            onClick={remove}
            data-testid="delete-customer"
            className="flex items-center gap-2 border border-red-500/40 text-red-300 uppercase tracking-widest text-xs py-3 px-4 hover:bg-red-500/10 transition-colors"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        {[
          ["Teléfono", customer.phone],
          ["Correo", customer.email],
          ["Dirección", customer.address],
          ["Alta", dateShort(customer.created_at)],
        ].map(([k, v]) => (
          <div key={k} className="bg-[#12100E] border border-white/5 p-4">
            <p className="text-[0.65rem] uppercase tracking-[0.35em] text-[#8A7D71] mb-1">
              {k}
            </p>
            <p className="text-[#F5F0E6]">{v || "—"}</p>
          </div>
        ))}
      </div>

      {/* Credit balance panel */}
      <div
        data-testid="credit-panel"
        className="relative overflow-hidden bg-gradient-to-br from-[#1A1612] via-[#12100E] to-[#1A1612] border border-[#C89B3C]/30 p-6 md:p-8 rounded-sm"
      >
        <div className="flex items-start justify-between flex-wrap gap-6">
          <div>
            <div className="flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.4em] text-[#C89B3C] mb-3">
              <Wallet size={14} /> Crédito a favor
            </div>
            <p
              data-testid="credit-balance"
              className={`font-serif text-5xl md:text-6xl font-black ${
                (customer.credit_balance || 0) > 0 ? "text-[#C89B3C]" : "text-[#F5F0E6]/40"
              }`}
            >
              {money(customer.credit_balance || 0)}
            </p>
            <p className="text-xs text-[#8A7D71] mt-2">
              Aplicable como método de pago en apartados.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => { setCreditForm({ amount: "", reason: "", sign: 1 }); setCreditOpen(true); }}
              data-testid="credit-add-btn"
              className="flex items-center gap-2 bg-[#C89B3C] hover:bg-[#E6BF5F] text-[#0A0908] uppercase tracking-widest text-xs font-bold py-3 px-5 transition-colors"
            >
              <TrendingUp size={14} /> Agregar
            </button>
            <button
              onClick={() => { setCreditForm({ amount: "", reason: "", sign: -1 }); setCreditOpen(true); }}
              data-testid="credit-remove-btn"
              className="flex items-center gap-2 border border-[#F5F0E6]/20 text-[#F5F0E6] hover:bg-[#F5F0E6]/5 uppercase tracking-widest text-xs py-3 px-5 transition-colors"
            >
              <TrendingDown size={14} /> Deducir
            </button>
          </div>
        </div>

        {creditHistory.length > 0 && (
          <details className="mt-5 group">
            <summary className="cursor-pointer text-xs uppercase tracking-[0.3em] text-[#8A7D71] hover:text-[#C89B3C]">
              Historial de crédito ({creditHistory.length})
            </summary>
            <ul data-testid="credit-history" className="mt-3 divide-y divide-white/5 text-sm">
              {creditHistory.slice(0, 15).map((e) => (
                <li key={e.id} className="py-2 flex items-start justify-between gap-4">
                  <div>
                    <p className="text-[#F5F0E6]">{e.reason}</p>
                    <p className="text-[0.65rem] text-[#8A7D71]">
                      {dateShort(e.timestamp)} · {e.user}
                    </p>
                  </div>
                  <div className="text-right">
                    <span
                      className={`font-mono text-sm ${
                        e.delta > 0 ? "text-emerald-400" : "text-red-400"
                      }`}
                    >
                      {e.delta > 0 ? "+" : ""}
                      {money(e.delta)}
                    </span>
                    <p className="text-[0.65rem] text-[#8A7D71]">
                      Saldo: {money(e.new_balance)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>

      {customer.notes && (
        <div className="bg-[#12100E] border border-white/5 p-5">
          <p className="text-[0.65rem] uppercase tracking-[0.35em] text-[#8A7D71] mb-2">
            Notas
          </p>
          <p className="text-[#B8A99A] whitespace-pre-wrap">{customer.notes}</p>
        </div>
      )}

      <div>
        <h2 className="font-serif text-2xl text-[#F5F0E6] mb-4">Apartados</h2>
        {layaways.length === 0 ? (
          <p className="text-sm text-[#8A7D71]">Sin apartados. Crea el primero.</p>
        ) : (
          <ul className="space-y-2" data-testid="layaways-list">
            {layaways.map((l) => (
              <li key={l.id}>
                <Link
                  to={`/admin/layaways/${l.id}`}
                  className="flex items-center justify-between bg-[#12100E] border border-white/5 hover:border-[#A66E38]/40 p-4 transition-colors"
                >
                  <div>
                    <p className="text-[#F5F0E6]">{l.invoice_number}</p>
                    <p className="text-xs text-[#8A7D71]">
                      {dateShort(l.created_at)} · {l.status} · {l.schedule}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[#F5F0E6]">{money(l.total_amount)}</p>
                    <p className="text-xs text-[#C89B3C]">
                      Pagado {money(l.paid_amount)}
                    </p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4 py-8 overflow-y-auto"
          data-testid="new-layaway-modal"
        >
          <form
            onSubmit={submit}
            className="w-full max-w-2xl bg-[#12100E] border border-[#A66E38]/30 p-8"
          >
            <h2 className="font-serif text-2xl text-[#F5F0E6] mb-6">
              Nuevo apartado
            </h2>
            <div className="space-y-3">
              <p className="text-xs uppercase tracking-widest text-[#B8A99A]">
                Productos
              </p>
              {form.items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2">
                  <input
                    className="col-span-6 bg-[#0A0908] border border-white/10 text-[#F5F0E6] px-3 py-2 outline-none focus:border-[#C89B3C]"
                    placeholder="Descripción"
                    value={it.description}
                    onChange={(e) => updateItem(i, "description", e.target.value)}
                    data-testid={`item-desc-${i}`}
                    required
                  />
                  <input
                    type="number"
                    min="1"
                    className="col-span-2 bg-[#0A0908] border border-white/10 text-[#F5F0E6] px-3 py-2 outline-none focus:border-[#C89B3C]"
                    placeholder="Cant."
                    value={it.qty}
                    onChange={(e) => updateItem(i, "qty", e.target.value)}
                    data-testid={`item-qty-${i}`}
                  />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="col-span-3 bg-[#0A0908] border border-white/10 text-[#F5F0E6] px-3 py-2 outline-none focus:border-[#C89B3C]"
                    placeholder="Precio"
                    value={it.unit_price}
                    onChange={(e) => updateItem(i, "unit_price", e.target.value)}
                    data-testid={`item-price-${i}`}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => removeItem(i)}
                    className="col-span-1 text-red-300 hover:bg-red-500/10"
                    aria-label="Quitar"
                  >
                    <Trash2 size={16} className="mx-auto" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addItem}
                data-testid="add-item"
                className="text-xs uppercase tracking-widest text-[#C89B3C] hover:underline"
              >
                + Agregar producto
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6">
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#B8A99A] mb-2">
                  Enganche
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.deposit_amount}
                  onChange={(e) =>
                    setForm({ ...form, deposit_amount: e.target.value })
                  }
                  data-testid="layaway-deposit"
                  className="w-full bg-[#0A0908] border border-white/10 text-[#F5F0E6] px-3 py-2 outline-none focus:border-[#C89B3C]"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#B8A99A] mb-2">
                  Frecuencia
                </label>
                <select
                  value={form.schedule}
                  onChange={(e) =>
                    setForm({ ...form, schedule: e.target.value })
                  }
                  data-testid="layaway-schedule"
                  className="w-full bg-[#0A0908] border border-white/10 text-[#F5F0E6] px-3 py-2 outline-none focus:border-[#C89B3C]"
                >
                  <option value="semanal">Semanal</option>
                  <option value="quincenal">Quincenal</option>
                  <option value="mensual">Mensual</option>
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#B8A99A] mb-2">
                  Plazo (días)
                </label>
                <input
                  type="number"
                  min="7"
                  max="365"
                  value={form.term_days}
                  onChange={(e) =>
                    setForm({ ...form, term_days: e.target.value })
                  }
                  data-testid="layaway-term"
                  className="w-full bg-[#0A0908] border border-white/10 text-[#F5F0E6] px-3 py-2 outline-none focus:border-[#C89B3C]"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-xs uppercase tracking-widest text-[#B8A99A] mb-2">
                Notas
              </label>
              <textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="layaway-notes"
                className="w-full bg-[#0A0908] border border-white/10 text-[#F5F0E6] px-3 py-2 outline-none focus:border-[#C89B3C] resize-none"
              />
            </div>

            <div className="flex items-center justify-between mt-6 pt-6 border-t border-white/5">
              <p className="text-[#F5F0E6]">
                Total: <span className="font-serif text-2xl">{money(total)}</span>
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="text-xs uppercase tracking-widest text-[#8A7D71] hover:text-[#F5F0E6] px-4"
                  data-testid="cancel-layaway"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="save-layaway"
                  className="bg-[#A66E38] hover:bg-[#B88655] disabled:opacity-50 text-[#F5F0E6] uppercase tracking-widest text-xs py-3 px-6 transition-colors"
                >
                  {saving ? "Guardando..." : "Crear apartado"}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {creditOpen && (
        <div
          data-testid="credit-modal"
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
          onClick={() => setCreditOpen(false)}
        >
          <form
            onSubmit={submitCredit}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#12100E] border border-[#C89B3C]/30 p-6 md:p-8 w-full max-w-md rounded-sm"
          >
            <p className="text-[0.65rem] uppercase tracking-[0.4em] text-[#C89B3C] mb-2">
              Crédito del cliente
            </p>
            <h3 className="font-serif text-2xl text-[#F5F0E6] mb-6">
              {creditForm.sign === 1 ? "Agregar crédito" : "Deducir crédito"}
            </h3>

            <label className="block text-[0.65rem] uppercase tracking-[0.35em] text-[#8A7D71] mb-1">
              Monto
            </label>
            <div className="flex items-center bg-[#0A0908] border border-white/10 focus-within:border-[#C89B3C]">
              <span className="pl-3 text-[#C89B3C]">$</span>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={creditForm.amount}
                onChange={(e) => setCreditForm({ ...creditForm, amount: e.target.value })}
                data-testid="credit-input-amount"
                autoFocus
                className="w-full bg-transparent text-[#F5F0E6] text-2xl font-serif px-2 py-3 outline-none"
                placeholder="0.00"
              />
            </div>

            <label className="block text-[0.65rem] uppercase tracking-[0.35em] text-[#8A7D71] mt-5 mb-1">
              Razón
            </label>
            <input
              value={creditForm.reason}
              onChange={(e) => setCreditForm({ ...creditForm, reason: e.target.value })}
              data-testid="credit-input-reason"
              placeholder={creditForm.sign === 1 ? "Devolución, pago de más, regalo..." : "Uso, corrección..."}
              className="w-full bg-[#0A0908] border border-white/10 text-[#F5F0E6] px-3 py-2 outline-none focus:border-[#C89B3C]"
            />

            <div className="flex items-center justify-between mt-6 pt-6 border-t border-white/5">
              <p className="text-sm text-[#8A7D71]">
                Saldo actual:{" "}
                <span className="text-[#C89B3C]">{money(customer.credit_balance || 0)}</span>
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setCreditOpen(false)}
                  className="text-xs uppercase tracking-widest text-[#8A7D71] hover:text-[#F5F0E6] px-4"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  data-testid="credit-submit"
                  className="bg-[#C89B3C] hover:bg-[#E6BF5F] text-[#0A0908] uppercase tracking-widest text-xs font-bold py-3 px-6 transition-colors"
                >
                  {creditForm.sign === 1 ? "Agregar" : "Deducir"}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
